import { useState } from "react";
import {
  FaArrowRight,
  FaEnvelope,
  FaExternalLinkAlt,
  FaFigma,
  FaMapMarkerAlt,
  FaPhone,
} from "react-icons/fa";
import {
  companyProjects,
  education,
  hobbyProjects,
  internshipsAndTraining,
  languages,
  personalInformation,
  skills,
  workExperience,
} from "../helper/constant";
import HeadingText from "./HeadingText";
import IntroCard from "./IntroCard";
import PhotoCollage from "./PhotoCollage";

const photos = [
  { src: "/IMG_6086.jpeg", alt: "City skyline" },
  { src: "/IMG_7248.jpeg", alt: "Mountain hike" },
  { src: "/IMG_8124.jpeg", alt: "Mountain hike" },
];

export const About = () => (
  <div className="animate-fadeIn max-w-4xl">
    <IntroCard photoSrc={"/IMG_4916.jpeg"} name={personalInformation.name} />
    <div className="flex flex-col mb-6">
      <h1 className="text-lg font-bold mb-0 text-brand-dark">
        {personalInformation.name}
      </h1>
      <h1 className="text-md text-neutral-500">(She / Her / Hers)</h1>
      <h1 className="text-md font-semibold text-neutral-500">
        {personalInformation.location}
      </h1>
    </div>
    <HeadingText
      font="font-bold"
      mb="mb-3"
      size="xs"
      color="text-neutral-400"
      className="uppercase tracking-[0.25em]"
      text="Professional Information"
    />
    <div className=" text-md text-neutral-600 leading-relaxed font-light mb-4">
      <p>{personalInformation.professionalInfo}</p>
    </div>{" "}
    <HeadingText
      font="font-bold"
      mb="mb-3"
      size="xs"
      color="text-neutral-400"
      className="uppercase tracking-[0.25em]"
      text="Personal Information"
    />
    <PhotoCollage photos={photos} />
    <div className="mb-4 text-md text-neutral-600 leading-relaxed font-light">
      <p>{personalInformation.personalInfo}</p>
    </div>{" "}
  </div>
);

const ProjectCard = ({ project, style }) => {
  return (
    <article
      style={style}
      className="opacity-0 animate-fadeInUp group relative overflow-hidden rounded-3xl bg-brand-dark border border-white/5 hover:border-white/15 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-black/40"
    >
      {/* Thumbnail */}
      <div className="relative h-[260px] overflow-hidden">
        <img
          src={project.thumbnail}
          alt={project.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/20 to-transparent" />

        {/* Status */}
        <div className="absolute top-5 left-5">
          <span className="px-3 py-1 rounded-full bg-black/40 backdrop-blur-md text-brand-cream text-xs font-medium border border-white/10">
            {project.status}
          </span>
        </div>

        {/* Featured Badge */}
        {project.featured && (
          <div className="absolute top-5 right-5">
            <span className="px-3 py-1 rounded-full bg-brand-cream text-brand-darker text-xs font-bold">
              Featured
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-7">
        {/* Title */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-2xl font-bold text-brand-cream group-hover:text-white transition-colors">
              {project.title}
            </h3>

            <p className="mt-1 text-sm text-neutral-400">{project.subtitle}</p>
          </div>

          <span className="text-xs text-neutral-500 whitespace-nowrap">
            {project.year}
          </span>
        </div>

        {/* Role */}
        <div className="mt-4">
          <span className="text-xs uppercase tracking-widest text-neutral-500">
            Role
          </span>

          <p className="mt-1 text-sm text-neutral-300">{project.role}</p>
        </div>

        {/* Description */}
        <p className="mt-5 text-neutral-400 leading-relaxed text-sm">
          {project.desc}
        </p>

        {/* Tools */}
        <div className="flex flex-wrap gap-2 mt-6">
          {project.tools?.map((tool) => (
            <span
              key={tool}
              className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-neutral-300"
            >
              {tool}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={project.prototypeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-cream text-brand-darker font-semibold hover:scale-[1.02] active:scale-[0.98] transition"
          >
            <FaFigma />
            View Prototype
          </a>

          {project.caseStudyUrl && (
            <a
              href={project.caseStudyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/10 text-neutral-300 hover:bg-white/5 transition"
            >
              Case Study
              <FaArrowRight size={12} />
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export const Projects = () => (
  <div className="animate-fadeIn space-y-20">
    {" "}
    <section>
      <div className="flex items-center gap-4 mb-10">
        <HeadingText text="Personal Projects" />
      </div>
      <div className="grid gap-8 sm:grid-cols-2">
        {hobbyProjects.map((p, i) => (
          <ProjectCard
            key={i}
            project={p}
            style={{ animationDelay: `${i * 100}ms` }}
          />
        ))}
      </div>
    </section>
  </div>
);

export const Skills = () => (
  <div className="animate-fadeIn space-y-12 pb-10">
    <HeadingText text="Skills" />
    <div className="grid gap-6 md:grid-cols-2">
      {Object.entries(skills).map(([category, items], i) => (
        <div
          key={category}
          style={{ animationDelay: `${i * 80}ms` }}
          className="opacity-0 animate-fadeInUp p-6 bg-brand-dark border border-neutral-800 rounded-3xl hover:border-brand-cream2/20 transition-all"
        >
          {/* Category Header */}
          <h4 className="text-[10px] uppercase tracking-[0.25em] text-brand-cream2 font-bold mb-5 opacity-70">
            {category.replace(/_/g, " ")}
          </h4>

          {/* Skill Pills */}
          <div className="flex flex-wrap gap-2">
            {items.map((skill) => (
              <span
                key={skill}
                className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-xl text-xs text-neutral-300 hover:bg-white/10 hover:border-white/20 hover:text-white transition-all duration-200 cursor-default"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const Languages = () => (
  <div className="animate-fadeIn">
    <HeadingText text="Languages" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {languages.map((lang, i) => (
        <div
          key={lang.name}
          style={{ animationDelay: `${i * 80}ms` }}
          className="opacity-0 animate-fadeInUp p-4 bg-brand-dark border border-neutral-800 rounded-2xl hover:border-brand-cream2/30 transition-all"
        >
          <div className="flex justify-between items-center mb-2">
            <span className="text-white font-medium">{lang.name}</span>
            <span className="text-xs font-mono text-white uppercase tracking-widest">
              {lang.level}
            </span>
          </div>
          <div className="h-1 w-full bg-neutral-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-cream2 rounded-full opacity-60"
              style={{ width: `${lang.strength}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TechStack = ({ tech }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const showExpand = tech.length > 5;
  const displayedTech = isExpanded ? tech : tech.slice(0, 5);

  return (
    <div className="flex flex-wrap gap-2 mt-4">
      {displayedTech.map((t, i) => (
        <span
          key={i}
          className="text-[10px] bg-brand-dark text-white px-3 py-1 rounded-full font-mono border border-brand-dark/10"
        >
          {t}
        </span>
      ))}
      {showExpand && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-[10px] text-neutral-500 font-mono transition-colors ml-2 underline underline-offset-4"
        >
          {isExpanded ? "Show Less" : `+${tech.length - 5} More`}
        </button>
      )}
    </div>
  );
};

export const Experience = () => {
  const ExperienceItem = ({ exp, delay = 0 }) => (
    <div
      style={{ animationDelay: `${delay}ms` }}
      className="opacity-0 animate-fadeInUp relative pl-8 border-l border-neutral-800 group hover:border-brand-dark transition-colors"
    >
      <div className="absolute -left-[5px] top-0 w-[9px] h-[9px] bg-brand-dark border border-neutral-700 rounded-full group-hover:bg-brand-dark transition-colors" />
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
        <div>
          <h3 className="text-xl font-bold text-brand-dark tracking-tight">
            {exp.role}
          </h3>
          <p className="text-brand-dark font-medium">{exp.company}</p>
        </div>
        <span className="text-xs font-mono text-white bg-brand-dark px-3 py-1 rounded-full border border-neutral-800 whitespace-nowrap">
          {exp.date}
        </span>
      </div>
      <p className="text-neutral-400 leading-relaxed text-sm max-w-3xl">
        {exp.desc}
      </p>
      <TechStack tech={exp.tech} />
    </div>
  );

  return (
    <div className="animate-fadeIn space-y-16">
      <section>
        <HeadingText text="Work Experience" />
        <div className="space-y-12">
          {workExperience.map((exp, i) => (
            <ExperienceItem key={i} exp={exp} delay={i * 100} />
          ))}
        </div>
      </section>

      <section>
        <HeadingText text="Internships & Training" />

        <div className="space-y-12">
          {internshipsAndTraining.map((exp, i) => (
            <ExperienceItem key={i} exp={exp} delay={i * 100} />
          ))}
        </div>
      </section>
    </div>
  );
};
export const Education = () => (
  <div className="animate-fadeIn space-y-12">
    <HeadingText text="Academic Foundation" />
    <div className="grid gap-8">
      {education.map((edu, i) => (
        <div
          key={i}
          style={{ animationDelay: `${i * 100}ms` }}
          className="opacity-0 animate-fadeInUp p-8 bg-brand-dark border border-neutral-800 rounded-3xl hover:border-brand-cream2/20 transition-all"
        >
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">{edu.school}</h3>
              <p className="text-white font-normal mt-1">{edu.degree}</p>
            </div>
            <span className="text-xs font-mono text-neutral-200">
              {edu.date}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-neutral-300 uppercase tracking-widest">
            <span>{edu.loc}</span>
            {edu.details && (
              <>
                <span className="h-1 w-1 bg-neutral-700 rounded-full" />
                <span className="text-neutral-400">{edu.details}</span>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const Links = () => (
  <div className="animate-fadeIn">
    <HeadingText text="Connect & Collaborate" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Contact Card */}
      <div className="p-8 bg-brand-dark border border-neutral-800 rounded-3xl space-y-4">
        <h4 className="text-xs uppercase tracking-[0.2em] text-white mb-6">
          Direct Contact
        </h4>
        <a
          href={`mailto:${personalInformation.email}`}
          className="flex items-center gap-4 text-neutral-300 hover:text-brand-cream transition-colors"
        >
          <div className="p-3 bg-black rounded-xl">
            <FaEnvelope />
          </div>
          <span className="text-sm truncate">{personalInformation.email}</span>
        </a>
        <div className="flex items-center gap-4 text-neutral-300 hover:text-brand-cream transition-colors">
          <div className="p-3 bg-black rounded-xl">
            <FaPhone />
          </div>
          <span className="text-sm text-white">
            {personalInformation.phone}
          </span>
        </div>
      </div>

      {/* Social Links */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {personalInformation.links.map((l, i) => (
          <a
            key={i}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ animationDelay: `${i * 80}ms` }}
            className="opacity-0 animate-fadeInUp flex flex-col items-center justify-center p-4 sm:p-6 bg-brand-dark border border-neutral-800 rounded-3xl hover:border-brand-cream2/30 hover:text-brand-cream transition-all group"
          >
            <l.icon
              size={28}
              className="mb-3 group-hover:scale-110 transition-transform"
            />
            <span className="text-[10px] font-bold uppercase tracking-widest">
              {l.label}
            </span>
          </a>
        ))}
      </div>
    </div>
  </div>
);
